/**
 * alignmentUtils.js — Shape alignment and distribution helpers
 *
 * Canvas dimensions (must match SlideCanvas.jsx):
 *   SLIDE_W = 960, SLIDE_H = 540
 *
 * All functions take:
 *   shapes   — array of shape objects { id, x, y, width, height, ... }
 *   mode     — alignment mode string (see below)
 *   onUpdate — (shapeId, updates) => void  callback to persist each change
 *
 * Supported modes:
 *   Multi-shape (2+):  'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom'
 *                      'distributeH' | 'distributeV'  (3+ shapes)
 *   Single-shape:      'slideH' | 'slideV' | 'slideCenter'
 */

export const SLIDE_W = 960;
export const SLIDE_H = 540;

// ─── Bounding box ─────────────────────────────────────────────────────────────

/**
 * Returns the axis-aligned bounding box that encloses all shapes.
 * Uses logical x/y/width/height (not rotated visual bounds) — matching
 * PowerPoint / Keynote alignment behaviour.
 */
export function getBoundingBox(shapes) {
  if (!shapes || shapes.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 };
  }
  const minX = Math.min(...shapes.map(s => s.x));
  const maxX = Math.max(...shapes.map(s => s.x + s.width));
  const minY = Math.min(...shapes.map(s => s.y));
  const maxY = Math.max(...shapes.map(s => s.y + s.height));
  return { minX, maxX, minY, maxY, width: maxX - minX, height: maxY - minY };
}

// ─── Main alignment function ──────────────────────────────────────────────────

/**
 * alignShapes — apply an alignment or distribution operation.
 *
 * @param {object[]} shapes   — shapes to align
 * @param {string}   mode     — alignment mode
 * @param {Function} onUpdate — (id, updates) => void
 */
export function alignShapes(shapes, mode, onUpdate) {
  if (!shapes || shapes.length === 0 || !onUpdate) return;

  // ── Single-shape: center on slide ────────────────────────────────────────
  if (shapes.length === 1) {
    const sh = shapes[0];
    if (mode === 'slideH') {
      onUpdate(sh.id, { x: Math.round((SLIDE_W - sh.width) / 2) });
    } else if (mode === 'slideV') {
      onUpdate(sh.id, { y: Math.round((SLIDE_H - sh.height) / 2) });
    } else if (mode === 'slideCenter') {
      onUpdate(sh.id, {
        x: Math.round((SLIDE_W - sh.width) / 2),
        y: Math.round((SLIDE_H - sh.height) / 2),
      });
    }
    return;
  }

  // ── Multi-shape alignment ─────────────────────────────────────────────────
  const { minX, maxX, minY, maxY } = getBoundingBox(shapes);
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  switch (mode) {
    // Horizontal axis
    case 'left':
      shapes.forEach(sh => onUpdate(sh.id, { x: Math.round(minX) }));
      break;

    case 'centerH':
      shapes.forEach(sh => onUpdate(sh.id, { x: Math.round(centerX - sh.width / 2) }));
      break;

    case 'right':
      shapes.forEach(sh => onUpdate(sh.id, { x: Math.round(maxX - sh.width) }));
      break;

    // Vertical axis
    case 'top':
      shapes.forEach(sh => onUpdate(sh.id, { y: Math.round(minY) }));
      break;

    case 'centerV':
      shapes.forEach(sh => onUpdate(sh.id, { y: Math.round(centerY - sh.height / 2) }));
      break;

    case 'bottom':
      shapes.forEach(sh => onUpdate(sh.id, { y: Math.round(maxY - sh.height) }));
      break;

    // Distribute horizontally — equal gaps between shapes (requires 3+)
    case 'distributeH': {
      if (shapes.length < 3) break;
      // Sort by left edge
      const sorted = [...shapes].sort((a, b) => a.x - b.x);
      // Outer shapes stay fixed; inner shapes get redistributed
      const leftEdge  = sorted[0].x;
      const rightEdge = sorted[sorted.length - 1].x + sorted[sorted.length - 1].width;
      const totalShapeWidth = sorted.reduce((sum, s) => sum + s.width, 0);
      const totalGap   = rightEdge - leftEdge - totalShapeWidth;
      const gapBetween = totalGap / (sorted.length - 1);
      let cursor = leftEdge + sorted[0].width + gapBetween;
      for (let i = 1; i < sorted.length - 1; i++) {
        onUpdate(sorted[i].id, { x: Math.round(cursor) });
        cursor += sorted[i].width + gapBetween;
      }
      break;
    }

    // Distribute vertically — equal gaps between shapes (requires 3+)
    case 'distributeV': {
      if (shapes.length < 3) break;
      const sorted = [...shapes].sort((a, b) => a.y - b.y);
      const topEdge    = sorted[0].y;
      const bottomEdge = sorted[sorted.length - 1].y + sorted[sorted.length - 1].height;
      const totalShapeHeight = sorted.reduce((sum, s) => sum + s.height, 0);
      const totalGap   = bottomEdge - topEdge - totalShapeHeight;
      const gapBetween = totalGap / (sorted.length - 1);
      let cursor = topEdge + sorted[0].height + gapBetween;
      for (let i = 1; i < sorted.length - 1; i++) {
        onUpdate(sorted[i].id, { y: Math.round(cursor) });
        cursor += sorted[i].height + gapBetween;
      }
      break;
    }

    default:
      break;
  }
}

// ─── Nudge helper ─────────────────────────────────────────────────────────────

/**
 * nudgeShape — move a shape by (dx, dy) pixels, clamped to slide bounds.
 */
export function nudgeShape(shape, dx, dy, onUpdate) {
  const nx = Math.max(0, Math.min(SLIDE_W - shape.width,  shape.x + dx));
  const ny = Math.max(0, Math.min(SLIDE_H - shape.height, shape.y + dy));
  onUpdate(shape.id, { x: Math.round(nx), y: Math.round(ny) });
}

// ─── Snap-to-grid helper ──────────────────────────────────────────────────────

/**
 * snapToGrid — round a value to the nearest grid increment.
 */
export function snapToGrid(value, grid = 8) {
  return Math.round(value / grid) * grid;
}
